import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './AnimatedHero.css';

const WORDS = [
  { text: 'green thumb', color: '#22c55e' }, // green
  { text: 'decor idea', color: '#f59e0b' }, // amber/orange
  { text: 'design style', color: '#3b82f6' }, // blue
  { text: 'room makeover', color: '#a855f7' }, // purple
];

const IMAGE_SETS = [
  [
    'https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1593696140826-c58b021acf8b?w=400&h=300&fit=crop&auto=format&q=80',
    'https://images.unsplash.com/photo-1496024840928-4c417adf211d?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=400&h=300&fit=crop&auto=format&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&h=300&fit=crop',
  ],
  [
    'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1560184897-ae75f418493e?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=400&h=300&fit=crop',
  ],
  [
    'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=400&h=300&fit=crop&auto=format&q=80',
    'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop&auto=format&q=80',
  ],
];

function AnimatedHero({ onGenerateClick }) {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentImageSet, setCurrentImageSet] = useState(0);
  const [imagesFadingOut, setImagesFadingOut] = useState([false, false, false, false, false]);
  const [imagesFadingIn, setImagesFadingIn] = useState([true, true, true, true, true]);

  const triggerAnimation = (newWordIndex) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    // Start fade out domino effect (left to right)
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        setImagesFadingOut(prev => {
          const newState = [...prev];
          newState[i] = true;
          return newState;
        });
      }, i * 100);
    }
    
    // After fade out completes (500ms), change word and image set, then fade in
    setTimeout(() => {
      setCurrentWordIndex(newWordIndex !== undefined ? newWordIndex : (prev) => (prev + 1) % WORDS.length);
      setCurrentImageSet((prev) => (prev + 1) % IMAGE_SETS.length);
      setImagesFadingOut([false, false, false, false, false]);
      setImagesFadingIn([false, false, false, false, false]);
      
      // Start fade in domino effect (left to right) after a brief pause
      setTimeout(() => {
        for (let i = 0; i < 5; i++) {
          setTimeout(() => {
            setImagesFadingIn(prev => {
              const newState = [...prev];
              newState[i] = true;
              return newState;
            });
          }, i * 100);
        }
        
        // Reset after fade in completes
        setTimeout(() => {
          setImagesFadingIn([true, true, true, true, true]);
          setIsAnimating(false);
        }, 600);
      }, 50);
    }, 500);
  };

  // Animate word changes every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      triggerAnimation();
    }, 3000);

    return () => clearInterval(interval);
  }, [isAnimating]);

  const currentWord = WORDS[currentWordIndex];
  const currentImages = IMAGE_SETS[currentImageSet];

  const handleDotClick = (index) => {
    if (index !== currentWordIndex && !isAnimating) {
      triggerAnimation(index);
    }
  };

  const handleArrowClick = (direction) => {
    if (!isAnimating) {
      if (direction === 'next') {
        triggerAnimation((currentWordIndex + 1) % WORDS.length);
      } else {
        triggerAnimation((currentWordIndex - 1 + WORDS.length) % WORDS.length);
      }
    }
  };

  return (
    <div className="animated-hero">
      <div className="hero-content">
        <h1 className="hero-title">
          Get your next{' '}
          <span
            className={`animated-word ${isAnimating ? 'bounce-out' : 'bounce-in'}`}
            style={{ color: currentWord.color }}
          >
            {currentWord.text}
          </span>
          {' '}idea
        </h1>

        {/* Dots indicator */}
        <div className="hero-dots">
          {WORDS.map((word, index) => (
            <button
              key={index}
              className={`dot ${index === currentWordIndex ? 'active' : ''}`}
              style={{
                backgroundColor: index === currentWordIndex ? currentWord.color : '#e5e7eb',
              }}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to ${word.text}`}
            />
          ))}
        </div>

        {/* Arrows */}
        <div className="hero-arrows">
          <button
            className="arrow-btn"
            onClick={() => handleArrowClick('prev')}
            style={{ color: currentWord.color }}
            aria-label="Previous"
          >
            <FiChevronLeft />
          </button>
          <button
            className="arrow-btn"
            onClick={() => handleArrowClick('next')}
            style={{ color: currentWord.color }}
            aria-label="Next"
          >
            <FiChevronRight />
          </button>
        </div>

        {/* Image Carousel */}
        <div className="hero-carousel">
          {currentImages.map((image, index) => (
            <div
              key={`${currentImageSet}-${index}`}
              className={`carousel-image ${
                imagesFadingOut[index] ? 'fade-out-left' : 
                imagesFadingIn[index] ? 'fade-in-right' : 
                'visible'
              }`}
            >
              <img src={image} alt={`Inspiration ${index + 1}`} />
              <div className="carousel-image-overlay">
                <button 
                  className="carousel-generate-btn"
                  onClick={onGenerateClick}
                >
                  Generate
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AnimatedHero;

